import { MainContentWrapper } from '@ydistri/ds';
import React, { useCallback, useEffect, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { useNavigate } from 'react-router';
import { skipToken } from '@reduxjs/toolkit/query';
import styled from 'styled-components';
import { RoutingPage, setSubpage } from '../../../routes/routerSlice';
import { useStableParams } from '../../../routes/useStableParams';
import { useAppDispatch } from '../../../store';
import { AvailableModals, openModal } from '../../Modals/modalSlice';
import NoAdministrationItemSelected from '../common/NoAdministrationItemSelected';
import { ProjectAdministrationSubpage } from '../projectAdministrationLib';
import {
  useCreateScopeMutation,
  useGetScopesQuery,
  useGetScopeUsagesQuery,
} from '../../../apis/apiScopes';
import ScopeEditorController from '../../../components/domain/Scopes/ScopeEditorController';
import ScopeCreateModal from './ScopeCreateModal';
import ScopeUsageSummary from './ScopeUsageSummary';
import ScopeCatalog from './catalog/ScopeCatalog';

const ScopeEditorPane = styled.div`
  flex: 1;
  min-width: 0;
`;

const ScopeAdministration: React.FC = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { projectShortName, scopeId } = useStableParams();
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const parsedScopeId = scopeId ? Number(scopeId) : undefined;
  const selectedScopeId = Number.isInteger(parsedScopeId) ? parsedScopeId : undefined;
  const { data: selections = [], isFetching: isFetchingSelections } = useGetScopesQuery(
    projectShortName ? { projectShortName } : skipToken,
  );
  const {
    currentData: scopeUsages = [],
    isError: isScopeUsagesError,
    isFetching: isFetchingScopeUsages,
    isLoading: isLoadingScopeUsages,
  } = useGetScopeUsagesQuery(
    projectShortName && selectedScopeId !== undefined
      ? { projectShortName, scopeId: selectedScopeId }
      : skipToken,
  );
  const [createScope, createState] = useCreateScopeMutation();

  useEffect(() => {
    dispatch(setSubpage(ProjectAdministrationSubpage.SCOPE));
  }, [dispatch]);

  const selectionUrl = useCallback(
    (id?: number): string =>
      `/${projectShortName ?? ''}/${RoutingPage.PROJECT_ADMINISTRATION}/${
        ProjectAdministrationSubpage.SCOPE
      }${id ? `/${id}` : ''}`,
    [projectShortName],
  );

  const createScopeWithName = useCallback(
    async (name: string) => {
      const created = await createScope({ name, isShared: true, sections: [] }).unwrap();
      setIsCreateModalOpen(false);
      navigate(selectionUrl(created.scopeId));
    },
    [createScope, navigate, selectionUrl],
  );

  const showEditor = selectedScopeId !== undefined && projectShortName !== undefined;
  let editorContent: React.ReactNode;

  if (showEditor) {
    editorContent = (
      <ScopeEditorController
        allowArchive
        allowSharing
        detailsContent={
          <ScopeUsageSummary
            isError={isScopeUsagesError}
            isFetching={isFetchingScopeUsages}
            isLoading={isLoadingScopeUsages}
            usages={scopeUsages}
          />
        }
        onEditAttributes={() =>
          dispatch(
            openModal({
              type: AvailableModals.SCOPE_ATTRIBUTES_EDIT,
              data: { scopeId: selectedScopeId },
            }),
          )
        }
        projectShortName={projectShortName}
        scopeId={selectedScopeId}
      />
    );
  } else {
    editorContent = (
      <NoAdministrationItemSelected
        message="Select a Scope"
        secondaryMessage="Or create a Scope to define rule sets and filters."
      />
    );
  }

  return (
    <>
      <Helmet title="Scope" />
      <MainContentWrapper>
        <ScopeCatalog
          data={selections}
          isFetching={isFetchingSelections}
          onCreate={() => setIsCreateModalOpen(true)}
          selectedScopeId={selectedScopeId}
          selectionUrl={selectionUrl}
        />
        <ScopeEditorPane>{editorContent}</ScopeEditorPane>
      </MainContentWrapper>
      {isCreateModalOpen && (
        <ScopeCreateModal
          isSaving={createState.isLoading}
          onClose={() => setIsCreateModalOpen(false)}
          onCreate={createScopeWithName}
        />
      )}
    </>
  );
};

export default ScopeAdministration;
